import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useLiveAvatarSession } from "../hooks/useLiveAvatarSession";
import {
  createLiveAvatarSessionToken,
  stopLiveAvatarSession,
} from "../utils/api";
import { formatUserName } from "../utils/helpers";
import { useAppContext } from "./AppContext";
import useNativeListener from "../hooks/useNativeListener";

export interface AIAvatarSessionContextValue {
  sessionToken: string | null;
  sessionId: string | null;
  isAgentConnecting: boolean;
  setIsAgentConnecting: (value: boolean) => void;
  agentError: string | null;
  setAgentError: (error: string | null) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  instructionVideoSrc: string | null;
  isVideoReady: boolean;
  isConnected: boolean;
  isVoiceChatActive: boolean;
  micPermissionError: boolean;
  transcription: string;
  avatarSpeaking: boolean;
  voiceChatMuted: boolean;
  startVoiceChat: () => Promise<void>;
  stopVoiceChat: () => void;
  initializeSession: () => Promise<void>;
  stopSession: () => Promise<void>;
  clearInstructionVideoSrc: () => void;
  setUserName: (userName: string) => void;
  setInstructionsEnabled: (instructionsEnabled: boolean) => void;
  sendMessage: (message: string) => void;
  repeat: (text: string) => void;
  interruptSpeech: (text: string) => void;
}

const AIAvatarSessionContext = createContext<AIAvatarSessionContextValue | null>(
  null
);

const instructionVideoDict_ar: Record<string, string> = {
  "سأظهر لك الفيديو الإرشادي لجهاز قياس الطول والوزن / مؤشر كتلة الجسم لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس ضغط الدم لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس الأكسجين في الدم لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس درجة حرارة الجسم لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس تخطيط القلب الكهربائي السريع لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس سكر الدم لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس حمض اليوريك لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "سأظهر لك الفيديو الإرشادي لجهاز قياس الهيموغلوبين لمساعدتك خطوة بخطوة.":
    "https://www.w3schools.com/html/mov_bbb.mp4",
};

const instructionVideoDict_en: Record<string, string> = {
  "i will show you the instructional video for the height and weight / bmi to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the blood pressure to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the blood oxygen (spo₂) to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the body temperature to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the rapid electrocardiogram (ecg) to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the blood glucose to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the uric acid to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
  "i will show you the instructional video for the hemoglobin to guide you step by step":
    "https://www.w3schools.com/html/mov_bbb.mp4",
};

interface AIAvatarSessionProviderProps {
  children: React.ReactNode;
}

export function AIAvatarSessionProvider({ children }: AIAvatarSessionProviderProps) {

  const { aiLanguage } = useAppContext();

  const [agentLanguage, setAgentLanguage] = useState<string>(aiLanguage === "ar" ? "ar" : "en");

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isAgentConnecting, setIsAgentConnecting] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [instructionVideoSrc, setInstructionVideoSrc] = useState<string | null>(
    null
  );
  const [userName, setUserName] = useState("");
  const [instructionsEnabled, setInstructionsEnabled] = useState(false);

  const {
    videoRef,
    audioRef,
    isVideoReady,
    isConnected,
    isVoiceChatActive,
    micPermissionError,
    transcription,
    avatarSpeaking,
    voiceChatMuted,
    startVoiceChat,
    stopVoiceChat,
    sendMessage,
    repeat,
    interruptSpeech,
  } = useLiveAvatarSession({
    sessionToken,
    userName: formatUserName(userName),
    onDisconnected: () => {
      setSessionToken(null);
      setSessionId(null);
    },
  });
  useNativeListener(sendMessage, aiLanguage);
  const initializeSession = useCallback(async () => {
    if (sessionToken) return;
    try {
      setIsAgentConnecting(true);
      setAgentError(null);
      const tokenData = await createLiveAvatarSessionToken(aiLanguage, "FULL");
      setSessionToken(tokenData.session_token);
      setSessionId(tokenData.session_id);
    } catch (err) {
      console.error("Avatar initialization error:", err);
      setAgentError(null); // Will be set by consumer with t()
      setIsAgentConnecting(false);
      throw err;
    } finally {
      setIsAgentConnecting(false);
    }
  }, [aiLanguage, sessionToken]);

  const stopSession = useCallback(async () => {
    if (sessionToken && sessionId) {
      try {
        await stopLiveAvatarSession(sessionToken, sessionId);
      } catch {
        // ignore
      }
      setSessionToken(null);
      setSessionId(null);
    }
  }, [sessionToken, sessionId]);

  const setAgentErrorState = useCallback((error: string | null) => {
    setAgentError(error);
  }, []);

  const clearInstructionVideoSrc = useCallback(() => {
    setInstructionVideoSrc(null);
  }, []);

  const value: AIAvatarSessionContextValue = {
    sessionToken,
    sessionId,
    isAgentConnecting,
    setIsAgentConnecting,
    agentError,
    setAgentError: setAgentErrorState,
    videoRef,
    audioRef,
    instructionVideoSrc,
    isVideoReady,
    isConnected,
    isVoiceChatActive,
    micPermissionError,
    transcription,
    avatarSpeaking,
    voiceChatMuted,
    startVoiceChat,
    stopVoiceChat,
    initializeSession,
    stopSession,
    clearInstructionVideoSrc,
    setUserName,
    setInstructionsEnabled,
    sendMessage,
    repeat,
    interruptSpeech,
  };

  useEffect(() => {
    const instructionVideoDict = aiLanguage === "ar" ? instructionVideoDict_ar : instructionVideoDict_en;
    setInstructionVideoSrc(null);
    if (instructionsEnabled) {
      Object.keys(instructionVideoDict).forEach((key) => {
        if (transcription.toLocaleLowerCase().includes(key)) {
          setTimeout(() => {
            setInstructionVideoSrc(instructionVideoDict[key]);
            stopVoiceChat();
          }, 2000)
        }
      });
    }
  }, [transcription, stopVoiceChat]);

  useEffect(() => {
    if (aiLanguage !== agentLanguage) {
      stopSession().then(() => {
        initializeSession();
        setAgentLanguage(aiLanguage);
      });
    } else {
      setAgentLanguage(aiLanguage);
    }
  }, [aiLanguage])

  return (
    <AIAvatarSessionContext.Provider value={value}>
      {children}
    </AIAvatarSessionContext.Provider>
  );
}

export function useAIAvatarSession(): AIAvatarSessionContextValue {
  const context = useContext(AIAvatarSessionContext);
  if (!context) {
    throw new Error(
      "useAIAvatarSession must be used within AIAvatarSessionProvider"
    );
  }
  return context;
}
